/**
 * Shared mapper from `SkillWithStatus.gating_reason` (closed enum, see
 * `packages/types/src/skill.ts`) to a merchant-readable sentence.
 *
 * Used by:
 *   - The readiness page's "Setup summary" skill line ("1 turned on ·
 *     0 live. Browse catalog needs a product catalog or a connected
 *     store.")
 *   - The readiness FixDrawer MCP block ("1 turned on, 0 live — your
 *     MCP server has no tools") so the drawer says the same thing the
 *     Settings → Skills tab says.
 *
 * The function returns `null` when there's no reason to show (either
 * the skill is live, or the reason is absent). Callers decide whether
 * to default to a generic "needs setup" line.
 */

import type { GatingReason } from '@sill/types';

/** Readable skill name for the seven canonical ids plus a sensible
 *  humanize fallback for merchant-authored custom skills. */
export function humanSkillName(skill_id: string): string {
  const label: Record<string, string> = {
    browse_catalog: 'Browse catalog',
    check_availability: 'Check availability',
    place_order: 'Place order',
    order_status: 'Order status',
    track_shipment: 'Track shipment',
    request_refund: 'Request refund',
    recommend: 'Recommend',
  };
  const direct = label[skill_id];
  if (direct !== undefined) return direct;
  return skill_id.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
}

/** Merchant-readable sentence for a single `gating_reason`. Null when
 *  we don't have a readable reason or the skill isn't gated. */
export function gatingReasonLine(
  skill_id: string,
  reason: GatingReason | undefined,
): string | null {
  if (reason === undefined) return null;
  const skill = humanSkillName(skill_id);
  if (reason === 'no_search_eligible_catalog') return `${skill} needs a product catalog or a connected store.`;
  if (reason === 'no_checkout_eligible_catalog') return `${skill} needs a buyable product in your catalog.`;
  if (reason === 'no_checkout_rule') return `${skill} needs a checkout rule in Rules.`;
  if (reason === 'missing_compliance_fields') return `${skill} needs your returns, terms and privacy URLs set.`;
  if (reason === 'no_refund_rail') return `${skill} needs a connected refund rail.`;
  if (reason === 'no_fulfillment_connector') return `${skill} needs a connected store (Shopify or WooCommerce).`;
  if (reason === 'no_adapter') return `${skill} is not available on your platform yet.`;
  if (reason === 'endpoint_unreachable') return `${skill}'s webhook endpoint didn't respond — check its URL.`;
  if (reason === 'below_tier_floor') return `${skill} is on a higher plan.`;
  return null;
}
